import { AlertTriangle, CheckCircle2, Info, Loader2, XCircle } from "lucide-react";

export type StatusNoticeTone = "loading" | "success" | "info" | "warning" | "error";

interface StatusNoticeProps {
  tone: StatusNoticeTone;
  message: string;
  className?: string;
}

const TONE_STYLES: Record<StatusNoticeTone, string> = {
  loading: "bg-cyan-500/10 border-cyan-400/35 text-cyan-200",
  success: "bg-emerald-500/10 border-emerald-400/35 text-emerald-200",
  info: "bg-blue-500/10 border-blue-400/35 text-blue-200",
  warning: "bg-amber-500/10 border-amber-400/35 text-amber-200",
  error: "bg-rose-500/10 border-rose-400/35 text-rose-200",
};

const ICON_STYLES: Record<StatusNoticeTone, string> = {
  loading: "text-cyan-300",
  success: "text-emerald-300",
  info: "text-blue-300",
  warning: "text-amber-300",
  error: "text-rose-300",
};

function ToneIcon({ tone }: { tone: StatusNoticeTone }) {
  if (tone === "loading") {
    return <Loader2 className={`h-4 w-4 animate-spin ${ICON_STYLES[tone]}`} />;
  }
  if (tone === "success") {
    return <CheckCircle2 className={`h-4 w-4 ${ICON_STYLES[tone]}`} />;
  }
  if (tone === "warning") {
    return <AlertTriangle className={`h-4 w-4 ${ICON_STYLES[tone]}`} />;
  }
  if (tone === "error") {
    return <XCircle className={`h-4 w-4 ${ICON_STYLES[tone]}`} />;
  }
  return <Info className={`h-4 w-4 ${ICON_STYLES[tone]}`} />;
}

export function StatusNotice({ tone, message, className = "" }: StatusNoticeProps) {
  return (
    <div
      role="status"
      aria-live={tone === "error" ? "assertive" : "polite"}
      className={`flex items-start gap-2 rounded-xl border px-3 py-2 text-sm ${TONE_STYLES[tone]} ${className}`}
    >
      <ToneIcon tone={tone} />
      <span>{message}</span>
    </div>
  );
}

export default StatusNotice;
